import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from './ui/card';
import { Button } from './ui/button';
import {
  User,
  Mail,
  Lock,
  Save,
  Loader2,
} from 'lucide-react';
import { getUserProfile, updateUserProfile, changeUserPassword } from '../api';

const UserProfile = () => {
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [isProfileEdited, setIsProfileEdited] = useState(false);

  // Cargar perfil de usuario
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const data = await getUserProfile();
        setUserProfile({
          name: data.name || '',
          email: data.email || '',
        });
      } catch (error) {
        toast.error('Error al cargar el perfil');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Manejar cambios en los campos del perfil
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setUserProfile(prev => ({
      ...prev,
      [name]: value
    }));
    setIsProfileEdited(true);
  };

  // Manejar cambios en los campos de contraseña
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Guardar cambios en el perfil
  const handleSaveProfile = async () => {
    if (!userProfile.name.trim() || !userProfile.email.trim()) {
      toast.error('Nombre y email son obligatorios');
      return;
    }

    setLoading(true);
    try {
      await updateUserProfile(userProfile);
      toast.success('Perfil actualizado correctamente');
      setIsProfileEdited(false);
    } catch (error) {
      toast.error('Error al actualizar el perfil');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Cambiar contraseña
  const handleChangePassword = async () => {
    // Validar que los campos no estén vacíos
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    // Validar que las contraseñas coincidan
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }

    // Validar longitud mínima
    if (passwordData.new_password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setPasswordLoading(true);
    try {
      await changeUserPassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Contraseña actualizada correctamente');
      // Resetear el formulario
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error('Error al cambiar la contraseña');
      console.error(error);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Perfil de Usuario */}
      <Card className="shadow-lg hover:shadow-xl transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center text-lg font-medium">
            <User className="h-5 w-5 mr-2" /> Información de Perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <div className="relative">
              <input
                type="text"
                name="name"
                value={userProfile.name}
                onChange={handleProfileChange}
                className="w-full p-2 pl-10 border rounded-md"
                placeholder="Tu nombre"
              />
              <User className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={userProfile.email}
                onChange={handleProfileChange}
                className="w-full p-2 pl-10 border rounded-md"
                placeholder="tucorreo@ejemplo.com"
              />
              <Mail className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button 
            onClick={handleSaveProfile}
            disabled={loading || !isProfileEdited}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </CardFooter>
      </Card>

      {/* Cambio de Contraseña */}
      <Card className="shadow-lg hover:shadow-xl transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center text-lg font-medium">
            <Lock className="h-5 w-5 mr-2" /> Cambiar Contraseña
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña Actual</label>
            <div className="relative">
              <input
                type="password"
                name="current_password"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                className="w-full p-2 pl-10 border rounded-md"
                placeholder="Ingresa tu contraseña actual"
              />
              <Lock className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nueva Contraseña</label>
            <div className="relative">
              <input
                type="password"
                name="new_password"
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                className="w-full p-2 pl-10 border rounded-md"
                placeholder="Al menos 8 caracteres"
              />
              <Lock className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Nueva Contraseña</label>
            <div className="relative">
              <input
                type="password"
                name="confirm_password"
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                className="w-full p-2 pl-10 border rounded-md"
                placeholder="Repite la nueva contraseña"
              />
              <Lock className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button 
            onClick={handleChangePassword}
            disabled={passwordLoading}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {passwordLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Actualizando...
              </>
            ) : (
              'Actualizar Contraseña'
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default UserProfile;